import React, { useState, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'common/components/modules/modalV2';
import './style.scss'
import Icon from 'common/components/elements/icons';
import Button from 'common/components/elements/buttons/primary';
import NewButton from 'common/components-v2/elements/button';

import { getLanguages, getCurrentLanguageByValue, onTranslteLanguage, setCookieValue } from 'common/utils/utils';
import cx from 'classnames'
import GenerateColor from 'common/utils/generateTextColor.js'

const languages = getLanguages();

const SelectLanguages = ({
   screenWidth,
   translteLanguage,
   onUpdateLanguage,
   className = 'mx-auto mt-4 mb-6 w-[150px]',
   isNewVersion,
   primaryColor,
   isShowAutoOption,
   languageDefaultText = 'Select',
   cookieValueBaseLanguageKey = 'en',
}) => {
   const [ready, setReady] = useState(false);
   const [animate, setAnimate] = useState('up');
   const [openSelectItems, setOpenSelectItems] = useState(false);
   const [language, setLanguage] = useState(languageDefaultText);
   const [selectDisabeld, setSelectDisabeld] = useState(false);


   let timerFetching = null;
   let languagesRef = createRef();

   const getCurrentLanguages = (bool) => {
      let defValue = document.documentElement.getAttribute('lang');
      if('en' === defValue && isShowAutoOption) {
         defValue = null
      }
      let result = document.cookie.match("(^|[^;]+)\\s*googtrans\\s*=\\s*([^;]+)");
      if(bool && result){
         defValue = result.pop().replace(`/${ cookieValueBaseLanguageKey }/`, '');
      }
      if(defValue) {
         let currentName = getCurrentLanguageByValue(defValue);
         return currentName;
      }

      return languageDefaultText
   }

   useEffect(() => {
      const target = document.getElementById("google_translate_element");
      if(!target) return;

      // Check if already present
      if(target.querySelector("select")) {
         setReady(true);
         const value = getCurrentLanguages(true);
         setLanguage(value)
         return;
      }

      // Watch for mutations
      const observer = new MutationObserver((mutations) => {
         for(const mutation of mutations) {
            if(mutation.type === "childList" && target.querySelector("select")) {
               setReady(true);
               const value = getCurrentLanguages(true);
               setLanguage(value)
               observer.disconnect();
               break;
            }
         }
      });

      observer.observe(target, { childList: true, subtree: true });

      return () => observer.disconnect();

   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if(screenWidth < 700){
         return
      }
      document.addEventListener('mousedown', (e) => handleClickOutside(e));
      return () => {
         clearTimeout(timerFetching);
         if(screenWidth > 701){
            document.removeEventListener('mousedown', (e) => handleClickOutside(e));
         }
      };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   function handleClickOutside(event) {
      if(languagesRef.current && !languagesRef.current.contains(event.target)) {
         setOpenSelectItems(!openSelectItems)
      }
      if(!event.target.closest(`#languag-dropdown`)) {
         setOpenSelectItems(false)
      }
   }

   const onSelectLanguages = (value, bool = true) => {
      let element = document.querySelector('#google_translate_element .goog-te-combo');
      if(element){
         setSelectDisabeld(true);
         onTranslteLanguage(value)
         if(!element.value) {
            onTranslteLanguage(value)
         }
         let currentLanguage = getCurrentLanguageByValue(element.value);
         if(currentLanguage === languageDefaultText && value) {
            currentLanguage = getCurrentLanguageByValue(value)
         }
         setLanguage(currentLanguage)
         if(onUpdateLanguage && bool){
            onUpdateLanguage(value)
         }
         if(screenWidth < 701) {
            onCloseContent()
         } else {
            setOpenSelectItems(false)
         }
         setSelectDisabeld(false);
      }

   }

   const deleteGoogTransCookies = () => {
      // expire cookie on current domain
      document.cookie = "googtrans=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;";

      // expire cookie on parent domain (handles `.example.com`)
      const hostname = window.location.hostname.replace(/^www\./, '');
      document.cookie = `googtrans=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${ hostname }`;
   };

   const onResetLanguageToAuto = () => {
      setCookieValue('googtrans', '', -1);
      deleteGoogTransCookies()
      if(onUpdateLanguage){
         onUpdateLanguage('auto', () => {
            window.location.reload();
         })
      } else {
         window.location.reload();
      }
   }

   const onCloseContent = () => {
      setAnimate('down')
      timerFetching = setTimeout(() => {
         setOpenSelectItems(false)
         setAnimate('up')
      }, 800)
   }

   if(!ready) return null;

   const view = (
      <div className='flex flex-col py-1 ' id='language'>
         <ul>
            {
               isShowAutoOption && languageDefaultText !== language && (
                  <li
                     role='presentation'
                     onClick={ (e) => {
                        onResetLanguageToAuto()
                     } }
                     className={ cx({
                        'flex py-2.5 px-4 cursor-pointer notranslate leading-5': true,
                        'dark:text-major-dark dark:hover:bg-hover-dark hover:bg-hover text-major': !isNewVersion,
                        'dark:text-gray-dark dark:hover:bg-gray-dark-100 hover:bg-gray-100 text-gray': isNewVersion,
                     }) }>
                     Auto
                  </li>
               )
            }
            {
               languages.map(((item, index) => {
                  return (
                     <li
                        data-google-lang={ item.value }
                        lang={ item.value }
                        role='presentation'
                        onClick={ (e) => {
                           onSelectLanguages(item.value, true)
                        } }
                        key={ index.toString() }
                        className={ cx({
                           'flex py-2.5 px-4 cursor-pointer notranslate leading-5': true,
                           'bg-hover dark:bg-hover-dark': item.name === language && !isNewVersion,
                           'bg-gray-100 dark:bg-gray-dark-100': item.name === language && isNewVersion,
                           'dark:text-major-dark dark:hover:bg-hover-dark hover:bg-hover text-major': !isNewVersion,
                           'dark:text-gray-dark dark:hover:bg-gray-dark-100 hover:bg-gray-100 text-gray': isNewVersion,
                        }) }>
                        {item.name}
                     </li>
                  )
               }))
            }
         </ul>
      </div>
   )

   if(isNewVersion){
      const getColor = (opacity) =>
         primaryColor ? (opacity ? GenerateColor.generateRGBColor(primaryColor, opacity) : primaryColor) : '';

      const shadowColor = getColor(0.2) || '#DEDDFF';

      return (
         <div
            ref={ languagesRef }
            className='flex ams-select-languages relative justify-center w-full'
            id='languag-dropdown'>

            <div
               style={ {
                  boxShadow: openSelectItems && primaryColor ? `0px 0px 0px 4px ${ shadowColor }` : '',
                  borderColor: openSelectItems && primaryColor ? primaryColor : '',
               } }
               className={ cx({
                  'bg-surface-200 dark:bg-surface-dark-150 rounded-label flex h-10 items-center justify-between px-[14px] w-full hover:bg-secondary-hover dark:hover:bg-secondary-hover-dark cursor-pointer': true,
                  'border-blue dark:border-blue-dark shadow-[0px_0px_0px_4px_#DEDDFF]': openSelectItems && !primaryColor,
                  'border': openSelectItems,
               }) }
               role='presentation'
               onClick={ selectDisabeld ? null : () => {
                  setOpenSelectItems(!openSelectItems)
               } }
            >
               <div className='flex gap-3 h-full items-center'>
                  <IconItem
                     name='translate' // NEED TO UPDATE /Icon/Globe, Language, Public, Earth
                  />
                  <span
                     className='text-gray leading-tight notranslate dark:text-gray-dark'
                  >
                     {language}
                  </span>

               </div>
               <IconItem
                  name='dropdown'
               />
            </div>
            {
               openSelectItems && (
                  <>
                     {
                        screenWidth < 1025 && (
                           <Modal
                              onClose={ onCloseContent }
                              roundedModal='lg'
                              animate={ animate }
                           >
                              <div className='flex px-3 py-2 border-b border-gray-200 dark:border-gray-dark-200 items-center gap-3'>
                                 <NewButton
                                    leftIconName='close'
                                    onClick={ onCloseContent }
                                    size='small'
                                    type='secondary'
                                 />
                                 <span className='text-base text-gray dark:text-gray-dark'>Select language </span>
                              </div>
                              {view}

                           </Modal>

                        )
                     }
                     {
                        screenWidth > 1024 && (
                           <div
                              className='absolute desktop-languages-content border border-gray-200 dark:border-gray-dark-200 bg-surface dark:bg-surface-dark w-full'>
                              {view}
                           </div>
                        )
                     }
                  </>
               )
            }
         </div>
      )
   }
   return (
      <div
         className={ `${ className }` }
      >
         <div
            ref={ languagesRef }
            className='flex ams-select-languages relative justify-center w-full'
            id='languag-dropdown'
         >
            <div
               className={ `flex h-10 items-center px-4 py-2.5 relative cursor-pointer select-none hover:bg-hover dark:hover:bg-hover-dark bg-panel dark:bg-panel-dark border dark:border-divider-dark border-divider rounded-lg ${ selectDisabeld ? 'opacity-50' : '' } w-full` }
               role='presentation'
               onClick={ selectDisabeld ? null : () => {
                  setOpenSelectItems(!openSelectItems)
               } }
            >
               <div
                  id='language-current-value'
                  className='w-full h-full absolute top-0 left-0'

               >

               </div>
               <div className='flex flex-1'>
                  <span
                     className='text-major leading-tight notranslate dark:text-major-dark'
                  >
                     {language}
                  </span>

               </div>
               <div className='h-6 w-6 flex items-center justify-center ml-2'>
                  <Icon
                     name={ `arrow-${ openSelectItems ? 'up' : 'down' }` }
                     size='xl'
                     color='secondary'
                     darkColor='secondary-dark'
                  />
               </div>
            </div>
            {
               openSelectItems && (
                  <>
                     {
                        screenWidth < 701 && (
                           <Modal
                              onClose={ onCloseContent }
                              roundedModal='lg'
                              animate={ animate }
                           >
                              <div className='flex px-3 py-4 border-b dark:border-divider-dark border-divider items-centar'>
                                 <div className='h-6 w-6 mr-4 '>
                                    <Button
                                       iconName='close'
                                       backgroundColor='trasnparent'
                                       onClick={ onCloseContent }
                                       classNames='h-full'
                                       padding='none'
                                       fontIconColor='major'
                                       darkIconcolor='major-dark'
                                    />
                                 </div>
                                 <span className='leading-tight text-major dark:text-major-dark'>Select language </span>
                              </div>
                              {view}

                           </Modal>

                        )
                     }
                     {
                        screenWidth > 700 && (
                           <div
                              className='absolute desktop-languages-content border border-divider dark:border-divider-dark bg-panel dark:bg-panel-dark shadow dark:shadow-dark w-full'>
                              {view}
                           </div>
                        )
                     }
                  </>
               )
            }

         </div>
      </div>
   )
};

const IconItem = ({
   name,
}) => {
   return (
      <div className='flex w-5 h-5 items-center justify-center'>
         <Icon
            name={ name }
            size='xl'
            color='gray-400'
            darkColor='gray-dark-400'

         />
      </div>
   )
}
IconItem.propTypes = {
   name: PropTypes.string,
}
SelectLanguages.propTypes = {
   screenWidth: PropTypes.number,
   translteLanguage: PropTypes.string,
   onUpdateLanguage: PropTypes.func,
   className: PropTypes.string,
   primaryColor: PropTypes.string,
   isNewVersion: PropTypes.bool,
   isShowAutoOption: PropTypes.bool,
   languageDefaultText: PropTypes.string,
   cookieValueBaseLanguageKey: PropTypes.string,
}
export default SelectLanguages;
